import React, { useEffect, useMemo } from 'react';
import { LoadingSpinner } from '../components/LoadingSpinner';
import RequireDesktopOrIpad from '../components/RequireDesktopOrIpad';
import { useIsMobile } from '../hooks/useIsMobile';
import { PRODUCTION_PATHS, resolveHostedAppUrl } from '../config/siteUrl';
import { auth } from '../services/firebase';

const CASEY_BOOTSTRAP_TOKEN_KEY = 'cognidoor.casey.bootstrap.firebase_token';

/**
 * BCG Casey Simulator launcher.
 * Redirects to the actual Casey app.
 * - On localhost: uses http://localhost:5181 (dev proxy target).
 * - Else (Vercel preview, etc.): uses production URL so we never send users to localhost.
 * - Override with REACT_APP_CASEY_APP_URL if needed.
 * - On mobile: show "requires computer or iPad" message instead of redirecting.
 */
function getCaseyAppUrl() {
  return resolveHostedAppUrl({
    envName: 'REACT_APP_CASEY_APP_URL',
    localhostUrl: 'http://localhost:5181',
    productionPath: PRODUCTION_PATHS.casey,
  });
}

export default function BCGCaseyAppPage() {
  const isMobile = useIsMobile(767);
  const appUrl = useMemo(getCaseyAppUrl, []);

  useEffect(() => {
    if (isMobile) return;
    async function handoff() {
      try {
        const currentUser = auth.currentUser;
        if (currentUser?.getIdToken) {
          const token = await currentUser.getIdToken();
          window.sessionStorage.setItem(CASEY_BOOTSTRAP_TOKEN_KEY, token);
        } else {
          window.sessionStorage.removeItem(CASEY_BOOTSTRAP_TOKEN_KEY);
        }
      } catch {
        window.sessionStorage.removeItem(CASEY_BOOTSTRAP_TOKEN_KEY);
      }
      window.location.href = appUrl;
    }
    void handoff();
  }, [appUrl, isMobile]);

  if (isMobile) {
    return <RequireDesktopOrIpad backTo="/bcg-casey-simulation" backLabel="View Casey simulation" />;
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 text-white p-6">
      <div className="flex flex-col items-center gap-4 max-w-sm text-center">
        <LoadingSpinner tone="dark" size="md" className="shrink-0" />
        <p className="text-sm font-medium text-white/90">Opening BCG Casey Simulator…</p>
        <p className="text-xs text-white/60">If you are not redirected, open the app directly.</p>
        <a
          href={appUrl}
          className="text-sm font-semibold text-emerald-300 hover:text-emerald-200 underline underline-offset-2"
        >
          Open BCG Casey Simulator
        </a>
      </div>
    </div>
  );
}
